import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

import {
  useGetEmployeeLeavesQuery,
  useApproveEmployeeLeaveMutation,
  useRejectEmployeeLeaveMutation,
  useAddEmployeeLeaveMutation,
} from 'redux/features/employeeLeaveApi';
import { useGetUsersQuery } from 'redux/features/users/userApi';
import { getSocket } from 'lib/socket';

import { useGetSettingsQuery } from 'redux/features/settings/settingsApi';
import dayjs from 'lib/dayjs';
import { useGetUnplannedLeavesQuery } from 'redux/features/employeeLeaveApi';
import { useBulkProcessDayLogsMutation } from 'redux/features/timeLogApi';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CommandPalette from '../../components/ui/CommandPalette';
import ApproveLeaveModal from './components/ApproveLeaveModal';
import LeaveDetailsModal from './components/LeaveDetailsModal';
import LeaveCard from '../../components/ui/LeaveCard';
import AddLeaveModal from '../holiday-management-employee-view/components/AddLeaveModal';
import DayLogEditModal from '../dashboard-overview/components/DayLogEditModal';
import PickDateModal from '../../components/ui/PickDateModal';
import { getUnplannedDates } from '../../utils/unplanned';
import { mapLeaveType, LEAVE_TYPES } from '../../utils/common';
import RejectLeaveModal from './components/RejectLeaveModal';
import Tabs from '../../components/Tabs';
import Pagination from '../../components/ui/Pagination';
import UnplannedLeaveTable from './components/UnplannedLeaveTable';
import MonthYearNavigation from '../../components/ui/MonthYearNavigation';

const formatContact = (contactStr) => {
  if (!contactStr) return '';
  try {
    const parsed = JSON.parse(contactStr);
    return `${parsed.name} - ${parsed.number}`;
  } catch {
    return contactStr;
  }
};

const AdminLeaveManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('PENDING');
  const [mainTab, setMainTab] = useState(searchParams.get('mainTab') || 'leave');
  const [workSchedule, setWorkScheduleState] = useState({
    dayStartTime: '',
    dayEndTime: '',
    breakResumeTime: '',
  });
  const [filters, setFilters] = useState({
    search: '',
    leaveType: '',
  });

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Unplanned leave pagination & search
  const [unplannedPage, setUnplannedPage] = useState(1);
  const [unplannedPageSize, setUnplannedPageSize] = useState(10);
  const [unplannedSearch, setUnplannedSearch] = useState('');

  // Work schedule configuration
  const { data: settingsData } = useGetSettingsQuery();

  // Add leave on behalf of employee
  const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedLeaveDate, setSelectedLeaveDate] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [lockedDates, setLockedDates] = useState(null);
  const [logTarget, setLogTarget] = useState(null); // { userId, date }
  const [pickLogRow, setPickLogRow] = useState(null); // unplanned leave with several days
  const [bulkProcessDayLogs] = useBulkProcessDayLogsMutation();

  // Expanded rows for multiple day leaves
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Modal states
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Build query params for server-side pagination & filtering
  const leavesQueryParams = {
    page: currentPage,
    limit: pageSize,
    ...(activeTab !== 'ALL' && { status: activeTab }),
    ...(filters.leaveType && { type: filters.leaveType }),
    ...(filters.search && { search: filters.search }),
  };

  // RTK Queries and Mutations
  const { data: leavesData } = useGetEmployeeLeavesQuery(leavesQueryParams);
  const {
    data: unplannedLeavesData,
    isLoading: isLoadingUnplanned,
    refetch: refetchUnplanned,
  } = useGetUnplannedLeavesQuery({
    month: currentMonth,
    year: currentYear,
    page: unplannedPage,
    limit: unplannedPageSize,
    ...(unplannedSearch && { search: unplannedSearch }),
  });
  const [approveLeave, { isLoading: isApproving }] = useApproveEmployeeLeaveMutation();
  const [rejectLeave, { isLoading: isRejecting }] = useRejectEmployeeLeaveMutation();
  const { data: employees } = useGetUsersQuery();
  const [addEmployeeLeave] = useAddEmployeeLeaveMutation();

  useEffect(() => {
    const socket = getSocket();
    if (socket) {
      const handleUserUpdate = () => {
        refetchUnplanned();
      };

      socket.on('userCheckedIn', handleUserUpdate);
      socket.on('userCheckedOut', handleUserUpdate);

      return () => {
        socket.off('userCheckedIn', handleUserUpdate);
        socket.off('userCheckedOut', handleUserUpdate);
      };
    }
  }, [refetchUnplanned]);

  // Tabs for leave status
  const tabs = [
    { id: 'PENDING', label: 'Pending', count: 0, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'APPROVED', label: 'Approved', count: 0, color: 'bg-green-100 text-green-800' },
    { id: 'REJECTED', label: 'Rejected', count: 0, color: 'bg-red-100 text-red-800' },
    { id: 'CANCELLED', label: 'Cancelled', count: 0, color: 'bg-gray-100 text-gray-800' },
    { id: 'ALL', label: 'All', count: 0, color: 'bg-blue-100 text-blue-800' },
  ];

  const handleAddLeaveOnBehalf = async (leaveData) => {
    try {
      const payload = {
        ...leaveData,
        status: 'APPROVED',
      };
      await addEmployeeLeave(payload).unwrap();
      toast.success('Leave added on behalf of employee successfully');
    } catch (error) {
      console.error('Failed to add leave:', error);
    }
  };

  // Reset to page 1 when filters or active tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filters.search, filters.leaveType]);

  // Reset unplanned page when month/year/search changes
  useEffect(() => {
    setUnplannedPage(1);
  }, [currentMonth, currentYear, unplannedSearch]);

  useEffect(() => {
    if (settingsData?.data) {
      setWorkScheduleState({
        dayStartTime: settingsData?.data?.day_start_time,
        dayEndTime: settingsData?.data?.day_end_time,
        breakResumeTime: settingsData?.data?.break_start_time,
      });
    }
  }, [settingsData]);

  useEffect(() => {
    const leaveId = searchParams.get('leaveId');
    if (leaveId && leavesData?.leaveRequests) {
      const leave = leavesData.leaveRequests.find((l) => l.id === Number(leaveId));
      if (leave) {
        setSelectedLeave(leave);
        setDetailsModalOpen(true);
      }
    }
  }, [searchParams, leavesData]);
  const removeLeaveId = () => {
    searchParams.delete('leaveId');
    setSearchParams(searchParams);
  };
  const handleApproveClick = (leave) => {
    setSelectedLeave(leave);
    setDetailsModalOpen(false);
    setApproveModalOpen(true);
  };

  const handleRejectClick = (leave) => {
    setSelectedLeave(leave);
    setDetailsModalOpen(false);
    setRejectModalOpen(true);
  };

  const handleApproveClickInCard = (leave) => {
    setSelectedLeave(leave);
    setApproveModalOpen(true);
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setDetailsModalOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedLeave) return;
    try {
      await approveLeave(selectedLeave.id).unwrap();
      setApproveModalOpen(false);
      setSelectedLeave(null);
    } catch (error) {
      console.error('Failed to approve leave:', error);
      toast.error('Failed to approve leave. Please try again.');
    }
  };

  const handleConfirmReject = async (reason) => {
    if (!selectedLeave) return;

    try {
      await rejectLeave({ id: selectedLeave.id, reason }).unwrap();
      setRejectModalOpen(false);
      setSelectedLeave(null);
    } catch (error) {
      console.error('Failed to reject leave:', error);
      toast.error('Failed to reject leave. Please try again.');
    }
  };

  const getLeaveTypeDisplay = (leave) => {
    return mapLeaveType(leave?.type);
  };

  const getStartTimeDisplay = (leave) => {
    // Handle datetime-based times
    if (leave?.start_date_time) {
      const date = new Date(leave?.start_date_time);
      if (!isNaN(date.getTime())) {
        return dayjs(date).format('DD/MM/YYYY hh:mm A');
      }
    }

    // Handle multiple day leaves - show first day
    if (leave?.dates && leave?.dates?.length > 0) {
      const firstDate = new Date(leave?.dates?.[0]);
      if (!isNaN(firstDate.getTime())) {
        // For multiple day leaves, show first day with start time
        const startTime =
          leave?.type === LEAVE_TYPES.HALF_DAY
            ? leave?.halfDayType === 'FIRST_HALF'
              ? workSchedule?.dayStartTime
              : workSchedule?.breakResumeTime
            : workSchedule?.dayStartTime;
        return `${firstDate.toLocaleDateString()} ${startTime}`;
      }
    }

    return 'N/A';
  };

  const getEndTimeDisplay = (leave) => {
    // Handle datetime-based times
    if (leave?.end_date_time) {
      const date = new Date(leave?.end_date_time);
      if (!isNaN(date.getTime())) {
        return dayjs(date).format('DD/MM/YYYY hh:mm A');
      }
    }

    // Handle multiple day leaves - show last day
    if (leave?.dates && leave?.dates?.length > 0) {
      const sortedDates = [...(leave?.dates || [])].sort();
      const lastDate = new Date(sortedDates?.[sortedDates?.length - 1]);
      if (!isNaN(lastDate.getTime())) {
        // For multiple day leaves, show last day with end time
        const endTime =
          leave?.type === LEAVE_TYPES.HALF_DAY
            ? leave?.halfDayType === 'FIRST_HALF'
              ? workSchedule?.breakResumeTime
              : workSchedule?.dayEndTime
            : workSchedule?.dayEndTime;
        return `${lastDate.toLocaleDateString()} ${endTime}`;
      }
    }

    return 'N/A';
  };

  const getDurationDisplay = (leave) => {
    // If duration is already calculated and stored, use it
    if (leave?.duration) {
      return leave?.duration;
    }

    // Calculate duration based on leave type using work schedule
    // Calculate duration based on leave type using work schedule
    switch (leave?.type) {
      case LEAVE_TYPES.FULL_DAY:
        return calculateWorkHours(workSchedule?.dayStartTime, workSchedule?.dayEndTime);

      case LEAVE_TYPES.HALF_DAY:
        if (leave?.halfDayType === 'FIRST_HALF') {
          return calculateWorkHours(workSchedule?.dayStartTime, workSchedule?.breakResumeTime);
        } else {
          return calculateWorkHours(workSchedule?.breakResumeTime, workSchedule?.dayEndTime);
        }

      case LEAVE_TYPES.SHORT:
        if (leave?.start_date_time && leave?.end_date_time) {
          return calculateDuration(leave?.start_date_time, leave?.end_date_time);
        }
        return 'N/A';

      case LEAVE_TYPES.LONG_TIME:
        if (leave?.start_date_time && leave?.end_date_time) {
          const days = calculateDaysDifference(leave?.start_date_time, leave?.end_date_time);
          const dailyHours = calculateWorkHours(
            workSchedule?.dayStartTime,
            workSchedule?.dayEndTime,
          );
          return `${days} day${days > 1 ? 's' : ''} (${dailyHours} per day)`;
        }
        return 'N/A';

      case LEAVE_TYPES.MULTIPLE_DAY:
        if (leave?.dates?.length > 0) {
          const dailyHours = calculateWorkHours(
            workSchedule?.dayStartTime,
            workSchedule?.dayEndTime,
          );
          return `${leave?.dates?.length} day${leave?.dates?.length > 1 ? 's' : ''} (${dailyHours} per day)`;
        }
        return 'N/A';

      default:
        return 'N/A';
    }
  };

  const calculateWorkHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';

    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;

    if (diffMs < 0) return '0 hours';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return diffMinutes > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffHours} hours`;
    } else {
      return `${diffMinutes} minutes`;
    }
  };

  const calculateDuration = (start_date_time, end_date_time) => {
    if (!start_date_time || !end_date_time) return '0 hours';

    const start = new Date(start_date_time);
    const end = new Date(end_date_time);
    const diffMs = end - start;

    if (diffMs < 0) return '0 hours';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return diffMinutes > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffHours} hours`;
    } else {
      return `${diffMinutes} minutes`;
    }
  };

  const calculateDaysDifference = (start_date_time, end_date_time) => {
    const start = new Date(start_date_time);
    const end = new Date(end_date_time);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: 'CircleCheckBig' },
      APPROVED: { color: 'bg-green-100 text-green-800', icon: 'CircleCheckBig' },
      REJECTED: { color: 'bg-red-100 text-red-800', icon: 'X' },
      CANCELLED: { color: 'bg-red-100 text-red-800', icon: 'X' },
    };

    const config = statusConfig?.[status] || statusConfig?.['PENDING'];

    return (
      <div
        className={`inline-flex items-center p-2  flex-row gap-2 rounded-full text-xs font-medium ${config?.color}`}
      >
        <Icon name={config?.icon} size={16} />
        {status}
      </div>
    );
  };

  const toggleRowExpansion = (leaveId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(leaveId)) {
      newExpandedRows.delete(leaveId);
    } else {
      newExpandedRows.add(leaveId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Update tabs with counts from API response
  const updatedTabs = tabs?.map((tab) => ({
    ...tab,
    count:
      tab?.id === 'ALL'
        ? Object.values(leavesData?.statusCounts || {}).reduce((sum, n) => sum + n, 0)
        : leavesData?.statusCounts?.[tab?.id] || 0,
  }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const mainTabs = [
    { key: 'leave', label: 'Leave', badge: leavesData?.leaveRequests?.length || 0 },
    {
      key: 'unplanned',
      label: 'Unplanned Leave',
      badge: unplannedLeavesData?.length || 0,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />
      <CommandPalette />

      {/* Main Content */}
      <div className="lg:ml-60 pt-16">
        <motion.div
          className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div className="mb-8" variants={itemVariants}>
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">Leave Management</h1>
                <p className="text-text-secondary">
                  Review, approve, or reject employee leave requests
                </p>
              </div>
              <button
                onClick={() => setIsAddLeaveModalOpen(true)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center space-x-2"
              >
                <Icon name="Plus" size={16} />
                <span>Add Leave</span>
              </button>
            </div>
          </motion.div>

          {/* Main Tabs */}
          <motion.div className="mb-6" variants={itemVariants}>
            <Tabs
              tabs={mainTabs}
              defaultTab={mainTab}
              tabName="mainTab"
              onChange={setMainTab}
              variant="default"
            />
          </motion.div>

          {mainTab === 'leave' ? (
            <>
              {/* Tabs */}
              <motion.div className="mb-6" variants={itemVariants}>
                <div className="flex flex-wrap gap-1 bg-secondary-50 p-1 rounded-lg w-full max-w-3xl">
                  {updatedTabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-200 flex items-center space-x-1.5 whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      <span>{tab?.label}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${tab?.color}`}
                      >
                        {tab?.count}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Filters */}
              <motion.div
                className="bg-surface rounded-lg border border-border p-4 mb-6"
                variants={itemVariants}
              >
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search Filter */}
                  <div className="flex-1">
                    <div className="relative">
                      <Icon
                        name="Search"
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
                      />
                      <input
                        type="text"
                        placeholder="Search by Employee Name or ID"
                        value={filters?.search}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, search: e?.target?.value }))
                        }
                        className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  {/* Leave Type Filter */}
                  <div className="lg:w-64">
                    <select
                      value={filters?.leaveType}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, leaveType: e?.target?.value }))
                      }
                      className="w-full p-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background"
                    >
                      <option value="">All Leave Types</option>
                      <option value={LEAVE_TYPES.FULL_DAY}>Full Day Leave</option>
                      <option value={LEAVE_TYPES.HALF_DAY}>Half Day Leave</option>
                      <option value={LEAVE_TYPES.LONG_TIME}>Long Time Leave</option>
                      <option value={LEAVE_TYPES.MULTIPLE_DAY}>Multiple Day Leave</option>
                      <option value={LEAVE_TYPES.SHORT}>Short Leave</option>
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* Table */}
              <motion.div
                className="bg-surface rounded-lg border border-border"
                variants={itemVariants}
              >
                {leavesData?.leaveRequests?.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-border border-b">
                        <thead className="bg-secondary-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              User
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              Leave Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              Start Time
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              End Time
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              Duration
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              Applied at
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              Modified at
                            </th>
                            <th
                              colSpan={2}
                              className="px-6  py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                            >
                              Reason
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-border">
                          {(leavesData?.leaveRequests || [])?.map((leave) => {
                            const leaveType = getLeaveTypeDisplay(leave);
                            const isMultipleDay =
                              leave?.type === LEAVE_TYPES.MULTIPLE_DAY && leave?.dates?.length > 0;
                            const isExpanded = expandedRows.has(leave?.id);
                            return (
                              <React.Fragment key={leave?.id}>
                                <tr
                                  onClick={() => handleRowClick(leave)}
                                  className="hover:bg-secondary-100 border-b cursor-pointer transition-colors"
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex flex-col">
                                      <span className="font-medium text-text-primary">
                                        {leave?.user?.first_name + ' ' + leave?.user?.last_name ||
                                          'Unknown Employee'}
                                      </span>
                                      <span className="text-xs text-text-secondary">
                                        {leave?.user?.employee_id}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                    <div className="flex items-center space-x-2">
                                      <span>{leaveType}</span>
                                      {isMultipleDay && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRowExpansion(leave?.id);
                                          }}
                                          className="text-primary hover:text-primary-600 transition-colors duration-200"
                                          title={isExpanded ? 'Hide dates' : 'Show all dates'}
                                        >
                                          <Icon
                                            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                                            size={16}
                                          />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                    {getStartTimeDisplay(leave)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                    {getEndTimeDisplay(leave)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                    {getDurationDisplay(leave)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                    {leave?.created_at
                                      ? dayjs(leave.created_at).format('DD/MM/YYYY hh:mm A')
                                      : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-text-secondary">
                                    {dayjs(leave?.created_at).isSame(leave?.updated_at)
                                      ? '—'
                                      : leave?.updated_at
                                        ? dayjs(leave.updated_at).format('DD/MM/YYYY hh:mm A')
                                        : 'N/A'}
                                  </td>
                                  <td
                                    colSpan={2}
                                    className="px-6 py-4 text-sm text-text-secondary max-w-xs truncate"
                                  >
                                    {leave?.reason || 'No reason provided'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {leave?.status === 'PENDING' ? (
                                      <div className="flex space-x-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleApproveClick(leave);
                                          }}
                                          className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors duration-200"
                                        >
                                          Approve
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRejectClick(leave);
                                          }}
                                          className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 transition-colors duration-200"
                                        >
                                          Reject
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center">
                                        {getStatusBadge(leave?.status)}
                                      </div>
                                    )}
                                  </td>
                                </tr>

                                {/* Expanded row for multiple day leaves */}
                                {isMultipleDay && isExpanded && (
                                  <tr className="bg-blue-50">
                                    <td colSpan="10" className="px-6 py-4">
                                      <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                          <Icon
                                            name="Calendar"
                                            size={16}
                                            className="text-blue-600"
                                          />
                                          <h4 className="text-sm font-semibold text-gray-900">
                                            Leave Schedule ({leave?.dates?.length} days)
                                          </h4>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                          {[...(leave?.dates || [])].sort().map((date, index) => {
                                            const isCancelled =
                                              leave?.canceled_dates?.includes(date);
                                            const dateObj = new Date(date);
                                            const dayName = dateObj.toLocaleDateString('en-US', {
                                              weekday: 'short',
                                            });
                                            const formattedDate = dateObj.toLocaleDateString(
                                              'en-US',
                                              {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                              },
                                            );
                                            return (
                                              <div
                                                key={index}
                                                className={`flex items-center justify-between p-3 bg-white border border-${isCancelled ? 'red' : 'blue'}-200 rounded-lg hover:shadow-sm transition-shadow`}
                                              >
                                                <div className="flex items-center space-x-2">
                                                  <div
                                                    className={`w-8 h-8 bg-${isCancelled ? 'red' : 'blue'}-100 rounded-lg flex items-center justify-center`}
                                                  >
                                                    <span
                                                      className={`text-xs font-bold text-${isCancelled ? 'red' : 'blue'}-600`}
                                                    >
                                                      {dateObj.getDate()}
                                                    </span>
                                                  </div>
                                                  <div>
                                                    <p
                                                      className={`text-sm font-medium text-${isCancelled ? 'red' : 'blue'}-900`}
                                                    >
                                                      {dayName}, {formattedDate}
                                                    </p>
                                                    <p
                                                      className={`text-xs text-${isCancelled ? 'red' : 'blue'}-500`}
                                                    >
                                                      {workSchedule?.dayStartTime} -{' '}
                                                      {workSchedule?.dayEndTime}
                                                    </p>
                                                  </div>
                                                </div>
                                                {isCancelled && (
                                                  <span
                                                    title="Cancelled"
                                                    className="inline-flex items-center px-2 py-1.5 rounded-2xl text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 uppercase tracking-wider"
                                                  >
                                                    X
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {leave?.contact_number && (
                                          <div className="flex items-center space-x-2 pt-2">
                                            <Icon
                                              name="Phone"
                                              size={16}
                                              className="text-blue-600"
                                            />
                                            <span className="text-sm text-gray-700">
                                              Contact: {formatContact(leave.contact_number)}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card List */}
                    <div className="lg:hidden p-4 space-y-4">
                      {(leavesData?.leaveRequests || [])?.map((leave) => (
                        <LeaveCard
                          key={leave?.id}
                          leave={leave}
                          isAdmin={true}
                          onApprove={handleApproveClickInCard}
                          onReject={handleRejectClick}
                          isLoading={isApproving || isRejecting}
                          processingId={selectedLeave?.id}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    <div className="px-4 pb-4">
                      <Pagination
                        currentPage={currentPage}
                        totalItems={leavesData?.total || 0}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                      />
                    </div>
                  </>
                ) : (
                  /* Empty State */
                  <div className="text-center py-12">
                    <Icon name="Calendar" size={48} className="mx-auto text-text-secondary mb-4" />
                    <h3 className="text-lg font-medium text-text-primary mb-2">
                      No {activeTab?.toLowerCase()} leaves found
                    </h3>
                    <p className="text-text-secondary">
                      {activeTab === 'PENDING'
                        ? 'There are no pending leave requests at the moment.'
                        : `No ${activeTab?.toLowerCase()} leave requests match your current filters.`}
                    </p>
                  </div>
                )}
              </motion.div>
            </>
          ) : (
            <motion.div
              className="bg-surface rounded-lg border border-border overflow-hidden"
              variants={itemVariants}
            >
              <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3 sm:items-center bg-secondary-50">
                <div className="flex items-center gap-2 flex-1">
                  <h3 className="text-lg font-semibold text-text-primary">Unplanned Leave</h3>
                  <div className="relative flex-1 max-w-xs">
                    <Icon
                      name="Search"
                      size={16}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      type="text"
                      placeholder="Search employee..."
                      value={unplannedSearch}
                      onChange={(e) => setUnplannedSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <MonthYearNavigation
                  selectedMonth={currentMonth}
                  selectedYear={currentYear}
                  setSelectedMonth={setCurrentMonth}
                  setSelectedYear={setCurrentYear}
                />
              </div>
              <UnplannedLeaveTable
                leaves={unplannedLeavesData?.data || unplannedLeavesData}
                onApprove={handleApproveClick}
                onReject={handleRejectClick}
                isLoading={isLoadingUnplanned}
                onAddLog={(leave) => {
                  const dates = getUnplannedDates(leave);
                  if (dates.length === 1) setLogTarget({ userId: leave.user_id, date: dates[0] });
                  else setPickLogRow(leave);
                }}
                onAddLeave={(leave) => {
                  setSelectedEmployeeId(leave.user_id);
                  setSelectedLeaveDate(new Date(leave.date));
                  setLockedDates(
                    leave.dates?.length
                      ? leave.dates
                      : [dayjs.utc(leave.date).format('YYYY-MM-DD')],
                  );
                  setIsAddLeaveModalOpen(true);
                }}
              />
              {(unplannedLeavesData?.total > 0 || unplannedLeavesData?.total === 0) && (
                <div className="p-4 border-t border-border">
                  <Pagination
                    currentPage={unplannedPage}
                    totalItems={unplannedLeavesData?.total || 0}
                    pageSize={unplannedPageSize}
                    onPageChange={setUnplannedPage}
                    onPageSizeChange={setUnplannedPageSize}
                  />
                </div>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Add Leave Modal */}
      {isAddLeaveModalOpen && (
        <AddLeaveModal
          isOpen={isAddLeaveModalOpen}
          onClose={() => {
            setIsAddLeaveModalOpen(false);
            setSelectedEmployeeId('');
            setSelectedLeaveDate(null);
            setSelectedLeaveType('');
            setLockedDates(null);
          }}
          onAddLeave={handleAddLeaveOnBehalf}
          isAdminMode={true}
          selectedEmployeeId={selectedEmployeeId}
          selectedDate={selectedLeaveDate}
          defaultLeaveType={selectedLeaveType}
          lockedDates={lockedDates}
          employees={employees?.data?.users || []}
        />
      )}

      <PickDateModal
        isOpen={!!pickLogRow}
        onClose={() => setPickLogRow(null)}
        dates={pickLogRow ? getUnplannedDates(pickLogRow) : []}
        title="Which day did the employee work?"
        onSelect={(date) => {
          setLogTarget({ userId: pickLogRow.user_id, date });
          setPickLogRow(null);
        }}
      />

      {/* Add Log (worked that day) */}
      {logTarget && (
        <DayLogEditModal
          isOpen
          onClose={() => setLogTarget(null)}
          date={logTarget.date}
          logs={[]}
          targetUserId={logTarget.userId}
          onSubmit={async (payload) => {
            try {
              await bulkProcessDayLogs({ userId: logTarget.userId, ...payload }).unwrap();
              toast.success('Logs updated successfully');
              setLogTarget(null);
            } catch (error) {
              toast.error(error?.data?.message || 'Failed to update logs');
            }
          }}
        />
      )}

      {/* Approve Leave Modal */}
      <ApproveLeaveModal
        isOpen={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        onConfirm={handleConfirmApprove}
        leave={selectedLeave}
        isLoading={isApproving}
        getStartTimeDisplay={getStartTimeDisplay}
        getEndTimeDisplay={getEndTimeDisplay}
        getDurationDisplay={getDurationDisplay}
      />

      {/* Reject Leave Modal */}
      <RejectLeaveModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        leaveDetails={selectedLeave}
        type="reject"
      />

      {/* Leave Details Modal */}
      <LeaveDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          removeLeaveId();
        }}
        leave={selectedLeave}
        onApprove={handleApproveClick}
        onReject={handleRejectClick}
        workSchedule={workSchedule}
      />

      {/* Reject Leave Modal */}
      {/* <ConfirmationModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        isLoading={isRejecting}
        title="Reject Leave Request"
        message="Are you sure you want to reject this leave request? This action cannot be undone."
      /> */}
    </div>
  );
};

export default AdminLeaveManagement;
